import isServer from '../../window/isServer';
import { safeJsonStringify } from '../safeJsonStringify';
class DebugManager {
    #registeredPrefixes = new Map();
    #activePrefixes = new Set();
    #activeVerbosePrefixes = new Set();
    debug = this.#createDebugger('', true);
    constructor() {
        if (!isServer()) {
            this.#clientSetup();
        }
    }
    get ALL_PREFIXES_STRING() {
        return [...this.#registeredPrefixes.keys()].join('|');
    }
    get GLOSSARY() {
        return Object.fromEntries(this.#registeredPrefixes);
    }
    isRegistered(prefix) {
        return this.#registeredPrefixes.has(prefix);
    }
    createDebugger(prefix, description) {
        if (!prefix || !prefix.trim()) {
            console.warn('[DEBUGGER] `createDebugger` called with an empty prefix.');
        }
        const upperPrefix = prefix.trim().toUpperCase();
        this.#registeredPrefixes.set(upperPrefix, description);
        return this.#createDebugger(upperPrefix);
    }
    toggle(prefix, options) {
        if (!this.#registeredPrefixes.has(prefix)) {
            console.warn(`[DEBUGGER] Prefix "${prefix}" is not registered. Registered: ${this.ALL_PREFIXES_STRING || '(none)'}`);
            return;
        }
        if (this.#activePrefixes.has(prefix)) {
            this.#activeVerbosePrefixes.delete(prefix);
            this.#activePrefixes.delete(prefix);
            return;
        }
        if (options?.verbose) {
            this.#activeVerbosePrefixes.add(prefix);
            this.#activePrefixes.add(prefix);
            return;
        }
        this.#activePrefixes.add(prefix);
    }
    #createDebugger(prefix, forceEnabled = false) {
        const logPrefix = prefix ? `[DEBUG][${prefix}]` : '[DEBUG]';
        const isStandardDebuggerEnabled = (prefix) => {
            return forceEnabled || this.#activePrefixes.has('*') || this.#activePrefixes.has(prefix);
        };
        const debug = (...args) => {
            if (isStandardDebuggerEnabled(prefix)) {
                console.log(logPrefix, ...args);
            }
        };
        debug.warn = (...args) => {
            if (isStandardDebuggerEnabled(prefix)) {
                console.warn(logPrefix, ...args);
            }
        };
        debug.error = (...args) => {
            if (isStandardDebuggerEnabled(prefix)) {
                console.error(logPrefix, ...args);
            }
        };
        debug.verbose = (...args) => {
            if (forceEnabled ||
                this.#activeVerbosePrefixes.has('*') ||
                this.#activeVerbosePrefixes.has(prefix)) {
                console.log(logPrefix, ...args);
            }
        };
        debug.json = (label, data) => {
            if (!isStandardDebuggerEnabled(prefix)) {
                return;
            }
            try {
                console.log(`${logPrefix} ${label}`, data, '\n', safeJsonStringify(Object.fromEntries(new URL(data).searchParams)));
            }
            catch {
                console.log(`${logPrefix} ${label}`, data, '\n', safeJsonStringify(data));
            }
        };
        debug.toggle = (options) => this.toggle(prefix, options);
        debug.isActive = () => this.#activePrefixes.has(prefix);
        debug.isVerboseActive = () => forceEnabled ||
            this.#activeVerbosePrefixes.has('*') ||
            this.#activeVerbosePrefixes.has(prefix);
        return debug;
    }
    #clientSetup() {
        const params = new URLSearchParams(window.location.search);
        const debugParam = params.get('debug');
        const verboseParam = params.get('debugVerbose');
        const populateSet = (param, set) => {
            if (!param)
                return;
            if (param === '*' || param.toLowerCase() === 'all') {
                set.add('*');
            }
            else {
                param.split('|').forEach((p) => p.trim() && set.add(p.trim().toUpperCase()));
            }
        };
        populateSet(debugParam, this.#activePrefixes);
        populateSet(verboseParam, this.#activeVerbosePrefixes);
        populateSet(verboseParam, this.#activePrefixes);
        window.__DEBUGGER__ = this;
    }
}
export const debugManager = !isServer() && window.__DEBUGGER__ ? window.__DEBUGGER__ : new DebugManager();
